/**
 * Microsoft To Do Service
 * Uses Microsoft Graph API to create tasks in the user's personal To Do list
 * 
 * Requires: User must be signed in with Microsoft account via MSAL
 * Scopes needed: Tasks.ReadWrite
 */

const GRAPH_TODO_ENDPOINT = 'https://graph.microsoft.com/v1.0/me/todo';

// Store msal instance reference
let _msalInstance = null;
let _account = null;

/**
 * Initialize the service with MSAL instance
 */
export function initMicrosoftTodoService(msalInstance, account) {
  _msalInstance = msalInstance;
  _account = account;
}

/**
 * Get access token for Microsoft Graph API
 */
async function getAccessToken() {
  if (!_msalInstance) throw new Error('Geen gebruiker ingelogd');
  const scopes = ['Tasks.ReadWrite'];
  // Zelfde patroon als getAuthHeaders (fix minder login-popups): het meegegeven
  // account kan een stale dubbele cache-entry zijn waarvan silent refresh faalt —
  // probeer dan de andere entries van DEZELFDE gebruiker voordat we een popup
  // doen (die de browser vaak blokkeert → To-Do's laden faalde stil).
  const all = _msalInstance.getAllAccounts() || [];
  const preferred = _msalInstance.getActiveAccount() || _account || (all.length === 1 ? all[0] : null);
  if (!preferred) throw new Error('Geen gebruiker ingelogd');
  const sameUser = [preferred, ...all.filter((a) =>
    a.homeAccountId !== preferred.homeAccountId &&
    (a.username || '').toLowerCase() === (preferred.username || '').toLowerCase())];
  let lastErr = null;
  for (const acc of sameUser) {
    try {
      const response = await _msalInstance.acquireTokenSilent({ scopes, account: acc });
      // Alleen actief zetten als het account ook echt WISSELT — setActiveAccount
      // vuurt een MSAL-event af dat React laat re-renderen; bij elke call zetten
      // gaf een render→load→render-lus die Graph liet throttlen (429).
      try {
        const working = response.account || acc;
        const active = _msalInstance.getActiveAccount();
        if (!active || active.homeAccountId !== working.homeAccountId) {
          _msalInstance.setActiveAccount(working);
        }
      } catch { /* ignore */ }
      return response.accessToken;
    } catch (e) { lastErr = e; }
  }
  console.log('[Microsoft Todo] Silent token faalde op alle accounts, popup…', lastErr?.message);
  const response = await _msalInstance.acquireTokenPopup({ scopes, account: preferred });
  return response.accessToken;
}

/**
 * Get all To Do lists for the user
 */
export async function getTodoLists() {
  const token = await getAccessToken();
  
  const response = await fetch(`${GRAPH_TODO_ENDPOINT}/lists`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get todo lists: ${response.statusText}`);
  }

  const data = await response.json();
  return data.value || [];
}

/**
 * Get the default "Tasks" list or create one
 */
export async function getDefaultList() {
  const lists = await getTodoLists();
  
  // Find the default "Tasks" list (usually displayName is "Tasks" or "Taken")
  let defaultList = lists.find(
    (list) => list.wellknownListName === 'defaultList' || 
              list.displayName === 'Tasks' || 
              list.displayName === 'Taken'
  );

  if (!defaultList && lists.length > 0) {
    // Use the first list if no default found
    defaultList = lists[0];
  }

  if (!defaultList) {
    // Create a new list if none exist
    defaultList = await createTodoList('Full Frame Taken');
  }

  return defaultList;
}

/**
 * Create a new To Do list
 */
export async function createTodoList(displayName) {
  const token = await getAccessToken();
  
  const response = await fetch(`${GRAPH_TODO_ENDPOINT}/lists`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ displayName }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create todo list: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a task in a specific list
 */
export async function createTask(listId, task) {
  const token = await getAccessToken();
  
  const taskBody = {
    title: task.title || task.name,
    body: task.body ? { content: task.body, contentType: 'text' } : undefined,
    importance: task.importance || 'normal', // low, normal, high
    status: 'notStarted',
    // Due date in ISO format
    dueDateTime: task.dueDate ? {
      dateTime: new Date(task.dueDate).toISOString(),
      timeZone: 'Europe/Amsterdam',
    } : undefined,
  };

  // Remove undefined values
  Object.keys(taskBody).forEach(key => taskBody[key] === undefined && delete taskBody[key]);

  const response = await fetch(`${GRAPH_TODO_ENDPOINT}/lists/${listId}/tasks`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(taskBody),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[Microsoft Todo] Create task failed:', errorText);
    throw new Error(`Failed to create task: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create multiple tasks in the default list
 * @param {Array} tasks - Array of task objects with { name, body?, dueDate?, importance? }
 * @returns {Promise<Array>} - Array of created tasks
 */
export async function createTasksBatch(tasks) {
  const defaultList = await getDefaultList();
  
  const createdTasks = [];
  const errors = [];

  // Create tasks one by one (Graph API doesn't support true batch for To Do)
  for (const task of tasks) {
    try {
      const created = await createTask(defaultList.id, task);
      createdTasks.push(created);
    } catch (error) {
      console.error(`[Microsoft Todo] Failed to create task "${task.name}":`, error);
      errors.push({ task: task.name, error: error.message });
    }
  }

  return {
    created: createdTasks,
    errors,
    listName: defaultList.displayName,
  };
}

/**
 * Get tasks from a specific list
 * @param {string} listId - The ID of the list to get tasks from
 * @param {boolean} includeCompleted - Whether to include completed tasks
 * @returns {Promise<Array>} - Array of tasks
 */
export async function getTasksFromList(listId, includeCompleted = false) {
  const token = await getAccessToken();
  
  // Filter to only get non-completed tasks unless specified
  // Subtaken (checklistItems) meeladen: Videobee-achtige taken met stappen (Joris, 6 sep).
  const filter = includeCompleted ? '?$top=200&$expand=checklistItems' : `?$top=200&$expand=checklistItems&$filter=status ne 'completed'`;
  
  const response = await fetch(`${GRAPH_TODO_ENDPOINT}/lists/${listId}/tasks${filter}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get tasks: ${response.statusText}`);
  }

  const data = await response.json();
  return data.value || [];
}

/**
 * Get all tasks from the default list
 * @param {boolean} includeCompleted - Whether to include completed tasks
 * @returns {Promise<Object>} - Object with tasks array and list info
 */
export async function getMyTodos(includeCompleted = false) {
  const defaultList = await getDefaultList();
  const tasks = await getTasksFromList(defaultList.id, includeCompleted);
  
  return {
    tasks,
    listName: defaultList.displayName,
    listId: defaultList.id,
  };
}

/**
 * Mark a task as completed
 * @param {string} listId - The list ID
 * @param {string} taskId - The task ID
 * @returns {Promise<Object>} - Updated task
 */
export async function completeTask(listId, taskId) {
  const token = await getAccessToken();
  
  const response = await fetch(`${GRAPH_TODO_ENDPOINT}/lists/${listId}/tasks/${taskId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status: 'completed' }),
  });

  if (!response.ok) {
    throw new Error(`Failed to complete task: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a task
 * @param {string} listId - The list ID
 * @param {string} taskId - The task ID
 */
export async function deleteTask(listId, taskId) {
  const token = await getAccessToken();
  
  const response = await fetch(`${GRAPH_TODO_ENDPOINT}/lists/${listId}/tasks/${taskId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete task: ${response.statusText}`);
  }

  return true;
}


/** Velden van een to-do bijwerken (title, body, dueDate, importance). */
/**
 * Einddatum van een To Do-taak als Nederlandse kalenderdag (YYYY-MM-DD).
 * Wij schrijven "2026-09-22T00:00:00 Europe/Amsterdam", Graph geeft terug "2026-09-21T22:00:00.0000000" met
 * timeZone "UTC". De eerste tien tekens nemen zette een taak dus een dag te vroeg — en een taak die je naar
 * vandaag sleepte stond na verversen op gisteren, buiten beeld (Joris, 21 sep 2026).
 */
export function todoDueDate(task) {
  const d = task?.dueDateTime;
  if (!d?.dateTime) return null;
  const raw = String(d.dateTime).replace(/\.\d+$/, '');
  const tz = d.timeZone || 'UTC';
  if (tz === 'UTC' || tz === 'tzone://Microsoft/Utc') {
    const dt = new Date(`${raw}Z`);
    if (Number.isNaN(dt.getTime())) return raw.slice(0, 10);
    return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit' }).format(dt);
  }
  return raw.slice(0, 10);
}

export async function updateTask(listId, taskId, patch) {
  const token = await getAccessToken();
  const body = {};
  if (patch.title !== undefined) body.title = patch.title;
  if (patch.body !== undefined) body.body = { content: patch.body || '', contentType: 'text' };
  if (patch.importance !== undefined) body.importance = patch.importance;
  if (patch.dueDate !== undefined) {
    body.dueDateTime = patch.dueDate ? { dateTime: `${String(patch.dueDate).slice(0, 10)}T00:00:00`, timeZone: 'Europe/Amsterdam' } : null;
  }
  const response = await fetch(`${GRAPH_TODO_ENDPOINT}/lists/${listId}/tasks/${taskId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`Failed to update task: ${response.statusText}`);
  return response.json();
}

/** Een lijst verwijderen (alleen zinvol als hij leeg is; Graph verwijdert anders ook de taken). */
export async function deleteTodoList(listId) {
  const token = await getAccessToken();
  const response = await fetch(`${GRAPH_TODO_ENDPOINT}/lists/${listId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) throw new Error(`Failed to delete list: ${response.statusText}`);
  return true;
}

/** Alle open to-do's over alle lijsten, met lijst-info erbij. */
export async function getAllTodos() {
  const lists = await getTodoLists();
  const isDefault = (l) => (l.wellknownListName || l.wellKnownListName || '') === 'defaultList' || l.displayName === 'Tasks' || l.displayName === 'Taken';
  const perList = await Promise.all(lists.map(async (l) => {
    try { return (await getTasksFromList(l.id)).map((t) => ({ task: t, list: l, isDefault: isDefault(l) })); }
    catch { return []; }
  }));
  return { lists: lists.map((l) => ({ ...l, isDefault: isDefault(l) })), tasks: perList.flat() };
}

/** Een afgevinkte to-do heropenen (ongedaan maken). */
export async function reopenTask(listId, taskId) {
  const token = await getAccessToken();
  const response = await fetch(`${GRAPH_TODO_ENDPOINT}/lists/${listId}/tasks/${taskId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: 'notStarted' }),
  });
  if (!response.ok) throw new Error(`Failed to reopen task: ${response.statusText}`);
  return response.json();
}

/** Subtaken ("stappen") van een To Do-taak. */
export function normaliseerChecklist(task) {
  return (task?.checklistItems || []).map((c) => ({ id: c.id, title: c.displayName || '', done: !!c.isChecked }));
}

async function graphChecklist(method, listId, taskId, itemId, payload) {
  const token = await getAccessToken();
  const url = `${GRAPH_TODO_ENDPOINT}/lists/${listId}/tasks/${taskId}/checklistItems${itemId ? `/${itemId}` : ''}`;
  const response = await fetch(url, {
    method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: payload ? JSON.stringify(payload) : undefined,
  });
  if (!response.ok) throw new Error(`Subtaak ${method} mislukt: ${response.statusText}`);
  return response.status === 204 ? null : response.json();
}
export const addChecklistItem = (listId, taskId, displayName) => graphChecklist('POST', listId, taskId, null, { displayName });
export const setChecklistItem = (listId, taskId, itemId, isChecked) => graphChecklist('PATCH', listId, taskId, itemId, { isChecked });
export const deleteChecklistItem = (listId, taskId, itemId) => graphChecklist('DELETE', listId, taskId, itemId);
